import { gql, useQuery, useLazyQuery } from '@apollo/client';
import get from 'lodash/get';
import { createRoute } from '../../lib/router';
import { getStatus } from '../../lib/status';
import * as paths from '../../config/paths';

export const SEARCH_USERS = gql`
  query fetchUsers($search: String, $pageSize: Int) {
    userSearch(search: $search, pageSize: $pageSize) {
      active
      createdAt
      firstName
      id
      lastName
      links {
        businessName
      }
      organizationUid
      partnerUid
      phone
      role
      uid
      updatedAt
    }
  }
`;

export const FETCH_CURRENT_USER = gql`
  query FetchCurrentUser {
    user {
      email
      firstName
      lastName
      organizationUid
      partnerUid
      phone
      roleSlug
      uid
    }
  }
`;

// These are the only User fields that actually work on this query
export const GET_USER_BY_UID = gql`
  query getUser($uid: UUID!) {
    user(uid: $uid) {
      reviewRocketId
      roleSlug
      uid
    }
  }
`;

export const GET_TEAM = gql`
  query GetOrganizationTeam(
    $cursor: String
    $organizationUid: UUID!
    $searchTerm: String
    $roleSlug: String
    $archived: Boolean
    $pageSize: Int
  ) {
    getOrganizationTeam(
      organizationUid: $organizationUid
      cursor: $cursor
      searchTerm: $searchTerm
      roleSlug: $roleSlug
      archived: $archived
      pageSize: $pageSize
    ) {
      users {
        avatarUrl
        email
        fullName
        firstName
        lastName
        uid
      }
    }
  }
`;

export const useFetchCurrentUser = () => {
  const { data, loading } = useQuery(FETCH_CURRENT_USER);
  const currentUser = get(data, 'user', {});
  return { currentUser, loadingCurrentUser: loading };
};

export const useUserReviewRocketIdQuery = uid => {
  const { data, loading } = useQuery(GET_USER_BY_UID, {
    skip: !uid,
    variables: { uid },
  });
  return { reviewRocketId: data?.user?.reviewRocketId, loading };
};

export const useSearchUsers = () => {
  const [searchUsers, { data, loading: searchUsersLoading }] = useLazyQuery(
    SEARCH_USERS,
    {
      fetchPolicy: 'network-only',
    }
  );

  const users = get(data, 'userSearch', []);
  const searchUsersData = users.map(
    ({
      uid,
      id,
      firstName,
      lastName,
      email,
      organizationUid,
      active,
      links,
      partnerUid,
    }) => {
      const status = getStatus(false, !active);
      let link = '';
      let organization = '';

      if (organizationUid) {
        organization = `${organizationUid} ${links.businessName}`;
        link = createRoute(paths.ORGANIZATION_USERS, { organizationUid });
      }

      if (partnerUid) link = createRoute(paths.PARTNER_ADMINS, { partnerUid });

      return {
        id: Number(id),
        uid,
        type: 'user',
        name: `${firstName} ${lastName}`,
        note: id.toString(),
        secondaryText: [email, organization],
        link,
        query: { search: `${firstName} ${lastName}` },
        status,
      };
    }
  );
  return {
    searchUsers,
    searchUsersData,
    searchUsersLoading,
  };
};

export const useGetTeam = () => {
  const [
    getTeamByOrgUid,
    {
      data,
      loading: getTeamByOrgUidLoading,
      error: getTeamByOrgUidError,
      refetch: getTeamByOrgUidRefetch,
    },
  ] = useLazyQuery(GET_TEAM);

  const getTeamByOrgUidData = get(data, 'getOrganizationTeam', {});

  return {
    getTeamByOrgUid,
    getTeamByOrgUidData,
    getTeamByOrgUidLoading,
    getTeamByOrgUidError,
    getTeamByOrgUidRefetch,
  };
};
